import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Layout } from './components/Layout';
import { NotificationPromptModal } from './components/NotificationPromptModal';
import { LoginPage } from './pages/LoginPage';
import { ActivatePage } from './pages/ActivatePage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import pulseLogo from './assets/pulse-logo.png';

// Super Admin Pages
import { SuperAdminDashboard } from './pages/superadmin/SuperAdminDashboard';
import { LicensesPage } from './pages/superadmin/LicensesPage';
import { AuditLogsPage } from './pages/superadmin/AuditLogsPage';

// Customer Admin Pages
import { CustomerDashboard } from './pages/admin/CustomerDashboard';
import { UsersPage } from './pages/admin/UsersPage';
import { DevicesPage } from './pages/admin/DevicesPage';
import { SettingsPage } from './pages/admin/SettingsPage';
import { LiveMonitorPage } from './pages/admin/LiveMonitorPage';
import { ActivityHistoryPage } from './pages/admin/ActivityHistoryPage';
import { UserDetailPage } from './pages/admin/UserDetailPage';
import { NotificationsPage } from './pages/admin/NotificationsPage';
import { KioskPage } from './pages/admin/KioskPage';
import { ReportsPage } from './pages/admin/ReportsPage';
import { FloorPlanPage } from './pages/admin/FloorPlanPage';

function NotificationNavigationHandler() {
  const navigate = useNavigate();

  React.useEffect(() => {
    const handlePending = () => {
      const pending = (window as any).__ckpulse_pending_route || localStorage.getItem('ckpulse_pending_route');
      if (pending) {
        (window as any).__ckpulse_pending_route = null;
        localStorage.removeItem('ckpulse_pending_route');
        navigate(pending);
      }
    };

    handlePending();
    const t1 = setTimeout(handlePending, 400);
    const t2 = setTimeout(handlePending, 1200);

    const onCustomNav = (e: any) => {
      if (e.detail && e.detail.url) {
        navigate(e.detail.url);
      }
    };

    window.addEventListener('ckpulse:navigate', onCustomNav);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('ckpulse:navigate', onCustomNav);
    };
  }, [navigate]);

  return null;
}

function ProtectedRoute({
  children,
  allowedRoles,
}: {
  children: React.ReactNode;
  allowedRoles: ('SUPER_ADMIN' | 'ADMIN' | 'AGENT')[];
}) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#090909] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <img src={pulseLogo} alt="CK Pulse" className="w-10 h-10 object-contain animate-pulse-subtle" />
          <span className="text-xs text-zinc-500 font-mono">Authenticating session...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!allowedRoles.includes(user.role)) {
    // If Super Admin tries to access Admin route, redirect to superadmin
    if (user.role === 'SUPER_ADMIN') {
      return <Navigate to="/superadmin" replace />;
    }
    // If Customer Admin tries to access Super Admin route, redirect to admin
    return <Navigate to="/admin" replace />;
  }

  return <>{children}</>;
}

function RootRedirect() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#090909] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-pulse-blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    if (user.role === 'SUPER_ADMIN') {
      return <Navigate to="/superadmin" replace />;
    }
    return <Navigate to="/admin/live" replace />;
  }

  return <Navigate to="/login" replace />;
}

import { LandingPage } from './pages/LandingPage';

function HomeRoute() {
  if (Capacitor.isNativePlatform()) {
    return <RootRedirect />;
  }
  return <LandingPage />;
}

import { ThemeProvider } from './context/ThemeContext';

export function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <NotificationPromptModal />
        <BrowserRouter>
          <NotificationNavigationHandler />
          <Routes>
            <Route path="/" element={<HomeRoute />} />
            <Route path="/website" element={<LandingPage />} />
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/activate" element={<ActivatePage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Super Admin Routes */}
            <Route
              path="/superadmin"
              element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<SuperAdminDashboard />} />
              <Route path="organizations" element={<SuperAdminDashboard />} />
              <Route path="licenses" element={<LicensesPage />} />
              <Route path="audit" element={<AuditLogsPage />} />
            </Route>

            {/* TV Kiosk / Wallboard Mode (Zero Chrome Fullscreen) */}
            <Route
              path="/admin/kiosk"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <KioskPage />
                </ProtectedRoute>
              }
            />

            {/* Customer Admin Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<CustomerDashboard />} />
              <Route path="live" element={<LiveMonitorPage />} />
              <Route path="floor-plan" element={<FloorPlanPage />} />
              <Route path="activity" element={<ActivityHistoryPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="users/:id" element={<UserDetailPage />} />
              <Route path="devices" element={<DevicesPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
